/**
 * Voice recorder helper — pure JS, no React.
 *
 * Sits behind {@link VoiceNoteButton} and is the testable seam for the
 * voice-note feature.  We deliberately keep all browser-global access
 * inside `VoiceRecorderSession` (via injected deps) so the unit tests can
 * run against stubs without needing jsdom.
 *
 * Capabilities provided:
 *   - `selectVoiceMime` — feature-detects a MediaRecorder MIME that works
 *     on the current browser (WebM-first, then MP4 for Safari iOS, then
 *     OGG for older Firefox).
 *   - `voiceMimeToExt`, `buildVoiceFilename` — filename helpers (kept pure
 *     so filename tests don't need a clock stub).
 *   - `VoiceRecorderSession` — the imperative wrapper around
 *     `MediaRecorder`; produces a single `File` on stop that the React
 *     button adds to the existing {@link usePromptInputAttachments}
 *     context.
 */

// ─── MIME selection ────────────────────────────────────────────────────────

/**
 * Mime types we try, in order.  WebM with Opus is by far the best voice
 * format on the web (good compression at low bitrates, well-supported by
 * Chromium/Firefox), but Safari iOS doesn't reliably produce WebM through
 * MediaRecorder, so we fall back to MP4 there.  OGG/Opus is a last-resort
 * niche for older Firefox builds.
 */
export const VOICE_MIME_CANDIDATES: readonly string[] = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
] as const;

/** Pick the first MIME that `check` says the browser supports, or null. */
export function selectVoiceMime(check: (mime: string) => boolean): string | null {
  for (const mime of VOICE_MIME_CANDIDATES) {
    if (check(mime)) {
      return mime;
    }
  }
  return null;
}

/** Default feature-detector — delegates to the runtime `MediaRecorder`. */
export function defaultIsTypeSupported(mime: string): boolean {
  if (typeof MediaRecorder === "undefined") {
    return false;
  }
  return MediaRecorder.isTypeSupported?.(mime) === true;
}

// ─── Filename helpers ───────────────────────────────────────────────────────

/** Map a chosen MediaRecorder MIME to a sane filename extension. */
export function voiceMimeToExt(mime: string): string {
  if (mime.startsWith("audio/webm")) return "webm";
  if (mime.startsWith("audio/mp4")) return "m4a";
  if (mime.startsWith("audio/ogg")) return "ogg";
  if (mime.startsWith("audio/wav")) return "wav";
  return "bin";
}

/** Build a stable, sortable filename like `voice-20240622-161425.webm`. */
export function buildVoiceFilename(mime: string, now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const ts =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  return `voice-${ts}.${voiceMimeToExt(mime)}`;
}

// ─── Imperative recorder session ────────────────────────────────────────────

/**
 * Error codes the React button can switch on for UI feedback.
 *
 *   - `permission_denied` — user denied the microphone prompt, or the
 *     browser blocked it (Safari private mode, etc.).
 *   - `no_audio_track` — `getUserMedia` succeeded but produced no audio
 *     tracks (very rare; surfaces as a generic error).
 *   - `recorder_failed` — `MediaRecorder.start()` / `dataavailable` /
 *     `stop` raised an unexpected error.
 *   - `unsupported` — MediaRecorder isn't available OR no candidate MIME
 *     was accepted; the button is rendered disabled in this case.
 */
export type VoiceRecorderErrorCode =
  | "permission_denied"
  | "no_audio_track"
  | "recorder_failed"
  | "unsupported";

export interface VoiceRecorderSessionDeps {
  getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  MediaRecorderCtor: typeof MediaRecorder;
}

export type VoiceRecorderSessionOptions = {
  mime: string;
  onError: (code: VoiceRecorderErrorCode, message: string) => void;
  /**
   * Optional dependency overrides for tests.  When omitted we read the
   * runtime globals (`navigator.mediaDevices.getUserMedia`,
   * `MediaRecorder`).
   */
  deps?: Partial<VoiceRecorderSessionDeps>;
};

/** Result returned to the React component on a successful stop. */
export interface VoiceRecordingResult {
  file: File;
  mime: string;
  /** Wall-clock time of the recording, used by chip UI to show duration. */
  durationMs: number;
  /** Bytes recorded; used by the chip UI to show a friendly size. */
  size: number;
}

type State = "idle" | "recording" | "stopping" | "errored";

/**
 * Wraps MediaRecorder lifecycle.  One session = one recording.  Designed
 * to be created on a "start" click and disposed on a "stop" click (or on
 * the React component's unmount cleanup effect).
 *
 * The class deliberately does NOT track React refs or hold UI state — it
 * is a plain JS object the React component holds in a ref.  All browser
 * globals (`navigator`, `MediaRecorder`, `MediaStream`) are read through
 * injectable deps so unit tests can pass stubs.
 */
export class VoiceRecorderSession {
  readonly mime: string;
  readonly #onError: (code: VoiceRecorderErrorCode, message: string) => void;
  readonly #deps: VoiceRecorderSessionDeps;

  #state: State = "idle";
  #stream: MediaStream | null = null;
  #recorder: MediaRecorder | null = null;
  #chunks: Blob[] = [];
  #startedAt: number | null = null;

  constructor(options: VoiceRecorderSessionOptions) {
    this.mime = options.mime;
    this.#onError = options.onError;

    // Resolve deps: explicit overrides win, then runtime globals.
    const mediaDevices =
      typeof navigator !== "undefined" ? navigator.mediaDevices : undefined;
    const getUserMedia =
      options.deps?.getUserMedia ??
      mediaDevices?.getUserMedia.bind(mediaDevices) ??
      (() => Promise.reject(new DOMException("getUserMedia not available", "NotAllowedError")));
    this.#deps = {
      getUserMedia,
      MediaRecorderCtor:
        options.deps?.MediaRecorderCtor ?? globalThis.MediaRecorder,
    };
  }

  /** Current lifecycle state.  Useful for tests and for the button UI. */
  get state(): State {
    return this.#state;
  }

  /** True if recording is in progress. */
  get isRecording(): boolean {
    return this.#state === "recording";
  }

  /**
   * The live `MediaStream` while recording, or `null` otherwise.
   * The caller can connect this to a Web Audio `AnalyserNode` for
   * real-time visualisation (waveform / level meters).
   */
  get stream(): MediaStream | null {
    return this.#stream;
  }

  /**
   * Begin recording.  Resolves when the MediaRecorder is actually running
   * (so the caller can flip its UI to the "recording" state synchronously
   * with the user click — important for perceived responsiveness).
   *
   * Idempotent: calling `start()` while already recording is a no-op
   * that resolves immediately, avoiding a regression where double-clicks
   * on the record button created two parallel streams.
   */
  async start(): Promise<void> {
    if (this.#state !== "idle") {
      return;
    }

    if (
      typeof this.#deps.getUserMedia !== "function" ||
      typeof this.#deps.MediaRecorderCtor !== "function"
    ) {
      this.#state = "errored";
      this.#onError(
        "unsupported",
        "MediaRecorder or microphone API is not available in this browser.",
      );
      return;
    }

    let stream: MediaStream;
    try {
      stream = await this.#deps.getUserMedia({ audio: true });
    } catch (err) {
      this.#state = "errored";
      // The browser surfaces user-denial as NotAllowedError / SecurityError;
      // everything else we report as a generic recorder failure.
      const domErr = err as DOMException;
      if (
        domErr?.name === "NotAllowedError" ||
        domErr?.name === "SecurityError" ||
        domErr?.name === "PermissionDeniedError"
      ) {
        this.#onError("permission_denied", "Microphone access was denied.");
      } else {
        this.#onError(
          "recorder_failed",
          `Failed to access microphone: ${domErr?.message ?? String(err)}`,
        );
      }
      return;
    }
    this.#stream = stream;

    const audioTracks = stream.getAudioTracks?.() ?? [];
    if (audioTracks.length === 0) {
      this.stopTracks();
      this.#state = "errored";
      this.#onError(
        "no_audio_track",
        "No audio track was returned by the microphone request.",
      );
      return;
    }

    let recorder: MediaRecorder;
    try {
      recorder = new this.#deps.MediaRecorderCtor(stream, {
        mimeType: this.mime,
      });
    } catch (err) {
      this.stopTracks();
      this.#state = "errored";
      this.#onError(
        "recorder_failed",
        `MediaRecorder construction failed: ${(err as Error)?.message ?? String(err)}`,
      );
      return;
    }
    this.#recorder = recorder;

    this.#chunks = [];
    recorder.addEventListener("dataavailable", (event) => {
      if (event.data && event.data.size > 0) {
        this.#chunks.push(event.data);
      }
    });
    recorder.addEventListener("error", (event) => {
      // The HTMLMediaElement-onerror-equivalent in MediaRecorder is opaque
      // — the only thing we reliably have is the error event itself.
      const e = event as unknown as { error?: Error; message?: string };
      this.#state = "errored";
      this.#onError(
        "recorder_failed",
        e.error?.message ?? e.message ?? "MediaRecorder encountered an error.",
      );
      this.stopTracks();
    });

    recorder.start();
    this.#startedAt =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    this.#state = "recording";
  }

  /**
   * Stop recording and assemble the captured chunks into a single `File`.
   *
   * Returns `null` if nothing was actually recorded (no chunks, or called
   * before {@link start} completed).  In that case the React button knows
   * not to add a chip.
   */
  async stop(): Promise<VoiceRecordingResult | null> {
    if (this.#state !== "recording" || !this.#recorder) {
      return null;
    }

    this.#state = "stopping";
    const recorder = this.#recorder;

    const result = await new Promise<VoiceRecordingResult | null>((resolve) => {
      const handleStop = () => {
        const chunks = this.#chunks;
        this.#chunks = [];
        if (chunks.length === 0) {
          resolve(null);
          return;
        }
        // BlobPart[] typing — Chromium / Firefox / Safari all accept an
        // array of Blob chunks here, which is the canonical way to
        // assemble a recorded MediaRecorder output.
        const blob = new Blob(chunks as BlobPart[], { type: this.mime });
        const filename = buildVoiceFilename(this.mime);
        const file = new File([blob], filename, {
          lastModified: Date.now(),
          type: this.mime,
        });
        const startedAt = this.#startedAt ?? Date.now();
        const durationMs =
          (typeof performance !== "undefined" ? performance.now() : Date.now()) -
          startedAt;
        resolve({
          durationMs,
          file,
          mime: this.mime,
          size: blob.size,
        });
      };

      recorder.addEventListener("stop", handleStop, { once: true });
      try {
        recorder.stop();
      } catch (err) {
        recorder.removeEventListener("stop", handleStop);
        this.#state = "errored";
        this.#onError(
          "recorder_failed",
          `MediaRecorder.stop() failed: ${(err as Error)?.message ?? String(err)}`,
        );
        this.stopTracks();
        resolve(null);
      }
    });

    this.stopTracks();
    this.#recorder = null;
    this.#state = "idle";

    return result;
  }

  /**
   * Cancel a recording without producing a file.  Used by the React
   * component's unmount effect so an in-flight recording doesn't leak
   * the microphone light when the user navigates away.
   *
   * Exposed publicly so a future "trash record" UI affordance can reuse
   * the same cleanup; it's also called by React unmount via useEffect.
   */
  abort(): void {
    if (this.#state === "idle") return;
    try {
      if (this.#recorder && this.#recorder.state !== "inactive") {
        this.#recorder.stop();
      }
    } catch {
      // Swallow — best-effort cancel.
    }
    this.stopTracks();
    this.#recorder = null;
    this.#chunks = [];
    this.#state = "idle";
  }

  /** Release media-stream tracks.  Safe to call repeatedly. */
  stopTracks(): void {
    if (!this.#stream) return;
    try {
      for (const track of this.#stream.getTracks()) {
        track.stop();
      }
    } catch {
      // Best-effort cleanup.
    }
    this.#stream = null;
  }
}
